import { useState, useEffect, useRef } from "react";
import {
    Box,
    Grid,
    Button,
    TextField,
    Typography,
    CircularProgress,
    Divider,
    Switch,
    FormControlLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from "@mui/material";
import Header from "../../components/Header";
import API from "../../api";
import { boxStyle } from "../../theme";
import IntelligenceTimeFilter from "../../components/IntelligenceTimeFilter";
import ImageTemplates from "../../components/ImageTemplates";
import { startToast, updateToastError } from "../../utils/notification-loading";
import dayjs from "dayjs";

// ─── Utility ────────────────────────────────────────────────────────────────
function getBrowserTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Rome";
}

const STORAGE_KEY = "report-test-ui:last";

function loadStorage() {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    } catch {
        return {};
    }
}


const ReportDashboard = () => {
    const saved = loadStorage();

    const [date, setDate] = useState({
        dateFrom: saved.dateFrom
            ? dayjs(saved.dateFrom)
            : dayjs().subtract(7, "day").startOf("day"),
        dateTo: saved.dateTo
            ? dayjs(saved.dateTo)
            : dayjs().subtract(1, "day").endOf("day"),
    });

    const [, setTimezone] = useState(getBrowserTimezone());
    const [summary, setSummary] = useState(saved.summary ?? "");
    const [includeTiresia, setIncludeTiresia] = useState(saved.includeTiresia ?? false);
    const [includeAssets, setIncludeAssets] = useState(false);
    const [openAssetsDialog, setOpenAssetsDialog] = useState(false);

    const [loading, setLoading] = useState(false);
    const [, setStatus] = useState("");
    const [, setIsError] = useState(false);
    const [pdfMeta, setPdfMeta] = useState(null);
    const lastUrlRef = useRef(null);
    const [elapsedSec, setElapsedSec] = useState(0);

    // Contatore del tempo trascorso mostrato durante la generazione: il report
    // può richiedere diversi minuti e il backend non espone una percentuale.
    useEffect(() => {
        if (!loading) return undefined;
        setElapsedSec(0);
        const startedAt = Date.now();
        const id = setInterval(() => {
            setElapsedSec(Math.floor((Date.now() - startedAt) / 1000));
        }, 1000);
        return () => clearInterval(id);
    }, [loading]);

    useEffect(() => {
        setTimezone(getBrowserTimezone());
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({
                    dateFrom: date.dateFrom?.format?.() ?? "",
                    dateTo: date.dateTo?.format?.() ?? "",
                    summary,
                    includeTiresia,
                    includeAssets,
                })
            );
        } catch {}
    }, [date, summary, includeTiresia, includeAssets]);

    const handleGeneratePDF = () => {
        const browserTimezone = getBrowserTimezone();
        const cleanSummary = summary.trim();

        const typeOfAnalysis = ["HighLights"];

        if (includeTiresia) {
            typeOfAnalysis.push("Tiresia");
        }

        if (includeAssets) {
            typeOfAnalysis.push("Assets");
        }

        const payload = {
            dateFrom: date.dateFrom.utc().format("YYYY-MM-DD HH:mm"),
            dateTo: date.dateTo.utc().format("YYYY-MM-DD HH:mm"),
            timezone: browserTimezone,
            typeOfAnalysis,
            ...(cleanSummary ? { summary: cleanSummary } : {}),
        };

        setTimezone(browserTimezone);
        setLoading(true);
        setStatus("Sending request…");
        setIsError(false);

        const started = performance.now();

        API.generatePDFReseller(payload)
            .then((response) => {
                const totalMs = Math.round(performance.now() - started);

                if (lastUrlRef.current) URL.revokeObjectURL(lastUrlRef.current);
                const url = URL.createObjectURL(response.data);
                lastUrlRef.current = url;

                setPdfMeta({ url, size: response.data.size, ms: totalMs });
                setStatus(`OK · ${totalMs} ms · ${(response.data.size / 1024).toFixed(1)} KB`);
                setIsError(false);
            })
            .catch(async (error) => {
                const ms = Math.round(performance.now() - started);
                let detail = error.message;

                if (error.response) {
                    try {
                        const text = await error.response.data.text();
                        detail = `HTTP ${error.response.status} (${ms} ms)\n\n${text}`;
                    } catch {
                        detail = `HTTP ${error.response.status} (${ms} ms)`;
                    }
                }

                setStatus(detail);
                setIsError(true);
            })
            .finally(() => setLoading(false));

    };

    const handleAssetsChange = (event) => {
        const checked = event.target.checked;

        if (!checked) {
            setIncludeAssets(false);
            return;
        }

        // Assets può essere attivato solo se il modulo ORION è abilitato -- senza
        // Orion non ci sono dati asset da includere nel report.
        API.getOrionVisibility()
            .then((res) => {
                if (res?.data?.visibility === true) {
                    setIncludeAssets(true);
                    setOpenAssetsDialog(true);
                } else {
                    startToast();
                    updateToastError("Assets can't be enabled — the ORION module must be turned on first.");
                }
            })
            .catch(() => {
                startToast();
                updateToastError("Couldn't verify ORION status — Assets was not enabled.");
            });
    };

    return (
        <Box m="20px">
            <Header title="Report Generator" />

            <Grid container spacing={2}>
                <Grid size={4}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box sx={{ ...boxStyle, p: 2 }}>
                            <IntelligenceTimeFilter
                                initialDateFrom={date.dateFrom}
                                initialDateTo={date.dateTo}
                                onDateChange={setDate}
                                maxTimeRange={{ value: 31, type: "day" }}
                            />
                        </Box>

                        <Box sx={{ ...boxStyle, p: 2 }}>
                            <Typography variant="subtitle2" mb={1}>
                                Options
                            </Typography>

                            <TextField
                                label="Summary"
                                value={summary}
                                onChange={(e) => setSummary(e.target.value)}
                                fullWidth
                                size="small"
                                multiline
                                rows={3}
                                placeholder="Optional free text printed on the cover"
                                sx={{ mb: 1 }}
                            />

                            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={includeTiresia}
                                            onChange={(e) => setIncludeTiresia(e.target.checked)}
                                        />
                                    }
                                    label="Tiresia"
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={includeAssets}
                                            onChange={handleAssetsChange}
                                        />
                                    }
                                    label="Orion"
                                />
                            </Box>
                        </Box>

                        <Dialog
                            open={openAssetsDialog}
                            onClose={() => setOpenAssetsDialog(false)}
                            maxWidth="xs"
                            fullWidth
                        >
                            <DialogTitle>Orion enabled</DialogTitle>

                            <DialogContent>
                                <DialogContentText>
                                    Enabling Orion may increase report generation time because the report may need to process a large volume of asset data.
                                </DialogContentText>
                            </DialogContent>

                            <DialogActions>
                                <Button onClick={() => setOpenAssetsDialog(false)} autoFocus>
                                    OK
                                </Button>
                            </DialogActions>
                        </Dialog>

                        <ImageTemplates />

                        <Button
                            variant="contained"
                            onClick={handleGeneratePDF}
                            disabled={loading}
                            fullWidth
                            size="large"
                            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                        >
                            {loading ? "Generating…" : "Generate PDF"}
                        </Button>
                    </Box>
                </Grid>

                <Grid size={8}>
                    <Box
                        sx={{
                            ...boxStyle,
                            height: "80vh",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <Box
                            sx={{
                                px: 2,
                                py: 1,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                {pdfMeta
                                    ? `PDF · ${(pdfMeta.size / 1024).toFixed(1)} KB · ${pdfMeta.ms} ms`
                                    : "No PDF yet."}
                            </Typography>
                        </Box>

                        <Divider />

                        {loading ? (
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 2,
                                }}
                            >
                                <CircularProgress size={36} />
                                <Typography variant="body1">
                                    Generating your report…
                                </Typography>
                                <Typography
                                    color="text.secondary"
                                    variant="body2"
                                    sx={{ textAlign: "center", maxWidth: 420 }}
                                >
                                    This may take several minutes depending on the date range
                                    and the amount of data. Please keep this page open.
                                </Typography>
                                <Typography color="text.secondary" variant="caption">
                                    Elapsed: {Math.floor(elapsedSec / 60)}:
                                    {String(elapsedSec % 60).padStart(2, "0")}
                                </Typography>
                            </Box>
                        ) : pdfMeta ? (
                            <iframe
                                key={pdfMeta.url}
                                src={pdfMeta.url + "#zoom=page-fit"}
                                title="PDF Preview"
                                style={{ flex: 1, width: "100%", border: 0 }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Typography color="text.secondary">
                                    Fill in the form and click "Generate PDF".
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ReportDashboard;